// PATH: src/api/apiClients.ts
import { useMemo } from "react";
import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  type AxiosInstance,
  type AxiosRequestHeaders,
} from "axios";
import { buildBaseUrl, apiTimeout, type ApiService } from "../config/config";
import { useNavigate } from "react-router-dom";
import { createAuthSync } from "../lib/authSync";
const sync = createAuthSync();

export const useApiClient = (service: ApiService = "embed"): AxiosInstance => {
  const handleError = useHandleError();

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const baseURL = buildBaseUrl(service);

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL,
      timeout: apiTimeout,
      // IMPORTANT: on s'appuie sur les cookies httpOnly -> il faut withCredentials
      withCredentials: true,
    });

    // === Intercepteur de requêtes ===
    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // NE PLUS injecter de Bearer: la session est portée par le cookie httpOnly
        const headers = (config.headers ?? {}) as AxiosRequestHeaders;
        headers["X-Timezone"] = timezone;
        config.headers = headers;
        return config;
      },
      (error: unknown) => Promise.reject(error)
    );

    // === Intercepteur de réponses ===
    instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        handleError(error);
        return Promise.reject(error);
      }
    );

    return instance;
  }, [baseURL, timezone, handleError]);

  return apiClient;
};

// ------------------------------------------------------
// Gestion centralisée des erreurs HTTP
// ------------------------------------------------------
const useHandleError = () => {
  const navigate = useNavigate();

  return (error: AxiosError | unknown): void => {
    const status = (error as AxiosError)?.response?.status;

    if (status === 401) {
      // Avec cookies httpOnly, on n'a plus de token à effacer côté front;
      // on redirige simplement vers /login.
      sync.publish({ type: "logout" });
      navigate("/login", { replace: true });
      return;
    }

    if (status === 403) {
      // Accès réservé aux administrateurs : on traite comme une fin de session
      // côté app (le cookie partagé reste valide pour les autres apps du domaine).
      // eslint-disable-next-line no-console
      console.warn("Accès interdit (403).");
      sync.publish({ type: "logout" });
      navigate("/login", { replace: true });
      return;
    }

    if (!(error as AxiosError)?.response) {
      // eslint-disable-next-line no-console
      console.error("Erreur réseau ou CORS:", error);
    }
  };
};
